import React from "react";
import { Box } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { T, WEEK_DAYS } from "../../constants/todoTokens";
import { buildGrid } from "./helpers";
import useTodoCalendar from "../../hooks/useTodoCalendar";

import CalendarHeader   from "./CalendarHeader";
import DayCell          from "./DayCell";
import WeekView         from "./WeekView";
import DayView          from "./DayView";
import ScheduleView     from "./ScheduleView";
import DayDetailModal   from "./DayDetailModal";
import NoDateStrip      from "./NoDateStrip";
import SkeletonCalendar from "./SkeletonCalendar";

const TodoCalendarView = ({ tasks = [], loading, onTaskClick, onArchive, onDelete, onRangeChange, isAdmin, currentUserId, onAssigneeStatusChanged, scheduleTasks = [], scheduleDate, scheduleLoading, scheduleLoadingMore, scheduleHasMore, onScheduleLoadMore }) => {
  const theme   = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    calView, setCalView,
    activeDate, selectDay,
    currentMonth, weekStart,
    direction,
    taskMap, noDateTasks, periodTaskCount,
    headerTitle, isCurrentPeriodNow,
    modalOpen, selectedDate, selectedDayTasks,
    goNext, goPrev, goToday,
    handleDayClick, closeModal,
  } = useTodoCalendar(tasks, onRangeChange);

  const todayKey = dayjs().format("YYYY-MM-DD");

  if (loading) return <SkeletonCalendar />;

  return (
    <>
      <Box
        sx={{
          bgcolor: T.surface,
          borderRadius: "16px",
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          boxShadow: T.shadow.md,
        }}
      >
        {/* Header — title is clickable to open mini-calendar date picker */}
        <CalendarHeader
          headerTitle={headerTitle}
          periodTaskCount={periodTaskCount}
          calView={calView}
          isCurrentPeriodNow={isCurrentPeriodNow}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
          onViewChange={setCalView}
          activeDate={activeDate}
          taskMap={taskMap}
          onSelectDay={selectDay}
        />

        {/* ── Month view ───────────────────────────────────────────────────────── */}
        {calView === "month" && (() => {
          const cells = buildGrid(currentMonth);
          return (
            <>
              {/* Day-name row */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${T.border}` }}>
                {WEEK_DAYS.map((day, i) => (
                  <Box
                    key={day}
                    sx={{
                      py: 1, textAlign: "center",
                      bgcolor: (i === 0 || i === 6) ? "#FAFBFC" : "#fff",
                      borderRight: i < 6 ? `1px solid ${T.border}` : "none",
                    }}
                  >
                    <Box component="span" sx={{
                      fontSize: { xs: "10px", sm: "11px" }, fontWeight: 700,
                      color: (i === 0 || i === 6) ? T.textMuted : T.textSub,
                      textTransform: "uppercase", letterSpacing: "0.06em", userSelect: "none",
                    }}>
                      {isSmall ? day[0] : day}
                    </Box>
                  </Box>
                ))}
              </Box>

              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={currentMonth.format("YYYY-MM")}
                  initial={{ opacity: 0, x: direction * 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -40 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                    {cells.map(({ date, current }, i) => {
                      const key      = date.format("YYYY-MM-DD");
                      const dayTasks = taskMap[key] || [];
                      return (
                        <DayCell
                          key={i}
                          date={date}
                          tasks={dayTasks}
                          current={current}
                          isToday={key === todayKey}
                          isMobile={isSmall}
                          onTaskClick={onTaskClick}
                          onDayClick={handleDayClick}
                        />
                      );
                    })}
                  </Box>
                </motion.div>
              </AnimatePresence>
            </>
          );
        })()}

        {/* ── Week view ─────────────────────────────────────────────────────────── */}
        {calView === "week" && (
          <WeekView
            weekStart={weekStart}
            direction={direction}
            taskMap={taskMap}
            onTaskClick={onTaskClick}
            onArchive={onArchive}
            onDelete={onDelete}
            isMobile={isSmall}
            isAdmin={isAdmin}
            currentUserId={currentUserId}
            onAssigneeStatusChanged={onAssigneeStatusChanged}
          />
        )}

        {/* ── Day view ──────────────────────────────────────────────────────────── */}
        {calView === "day" && (
          <DayView
            activeDate={activeDate}
            taskMap={taskMap}
            direction={direction}
            onTaskClick={onTaskClick}
            onArchive={onArchive}
            onDelete={onDelete}
            isAdmin={isAdmin}
            currentUserId={currentUserId}
            onAssigneeStatusChanged={onAssigneeStatusChanged}
          />
        )}

        {/* ── Schedule view ─────────────────────────────────────────────────────── */}
        {calView === "schedule" && (
          <ScheduleView
            tasks={scheduleTasks}
            scheduleDate={scheduleDate}
            loading={scheduleLoading}
            loadingMore={scheduleLoadingMore}
            hasMore={scheduleHasMore}
            onLoadMore={onScheduleLoadMore}
            onTaskClick={onTaskClick}
            onArchive={onArchive}
            onDelete={onDelete}
            isAdmin={isAdmin}
            currentUserId={currentUserId}
            onAssigneeStatusChanged={onAssigneeStatusChanged}
          />
        )}
      </Box>

      {/* Tasks with no date at all */}
      <NoDateStrip tasks={noDateTasks} onTaskClick={onTaskClick} />

      {/* Day-detail modal (month view cell clicks) */}
      <DayDetailModal
        open={modalOpen}
        date={selectedDate}
        tasks={selectedDayTasks}
        onClose={closeModal}
        onTaskClick={onTaskClick}
        onArchive={onArchive}
        onDelete={onDelete}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        onAssigneeStatusChanged={onAssigneeStatusChanged}
      />
    </>
  );
};

export default React.memo(TodoCalendarView);
